import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { notificationsApi } from "@/services/api";

type FilterType = "all" | "jobs" | "billing" | "field" | "system";

const filterLabels: { key: FilterType; label: string; icon: string }[] = [
  { key: "all",     label: "All",    icon: "ri-apps-line" },
  { key: "jobs",    label: "Jobs",   icon: "ri-file-list-3-line" },
  { key: "billing", label: "Bill",   icon: "ri-bill-line" },
  { key: "field",   label: "Field",  icon: "ri-map-pin-user-line" },
  { key: "system",  label: "Sys",    icon: "ri-settings-4-line" },
];

const typeToFilter: Record<string, FilterType> = {
  job_created:   "jobs",
  status_update: "jobs",
  payment:       "billing",
  approval:      "billing",
  delivery:      "jobs",
  client:        "system",
  deadline:      "jobs",
  assignment:    "jobs",
  system:        "system",
};

const typeIcon: Record<string, { icon: string; color: string }> = {
  job_created:   { icon: "ri-file-add-line",           color: "#d97706" },
  status_update: { icon: "ri-refresh-line",             color: "#b45309" },
  payment:       { icon: "ri-money-rupee-circle-line",  color: "#f59e0b" },
  approval:      { icon: "ri-checkbox-circle-line",     color: "#92400e" },
  delivery:      { icon: "ri-truck-line",               color: "#d97706" },
  client:        { icon: "ri-user-add-line",            color: "#78350f" },
  deadline:      { icon: "ri-alarm-warning-line",       color: "#ef4444" },
  assignment:    { icon: "ri-user-follow-line",         color: "#d97706" },
  system:        { icon: "ri-settings-4-line",          color: "#92400e" },
};

interface ActivityItem {
  id: number;
  type: string;
  message: string;
  user: string;
  avatar: string;
  time: string;
  color: string;
}

export default function CrossModuleActivity() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>("all");
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const notifs = await notificationsApi.getAll();
        const notifActivities: ActivityItem[] = (notifs || []).slice(0, 15).map((n: Record<string, unknown>, i: number) => {
          const actorName = (n.actor as string) ?? "System";
          const nType = n.type as string;
          return {
            id: 100 + i,
            type: nType === "assignment" ? "assignment"
              : nType === "payment" ? "payment"
              : nType === "status" ? "status_update"
              : "system",
            message: n.message as string,
            user: actorName,
            avatar: actorName.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase(),
            time: new Date(n.time as string).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
            color: "#d97706",
          };
        });
        setActivities(notifActivities);
      } catch {
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, []);

  const filtered = filter === "all"
    ? activities
    : activities.filter((a) => typeToFilter[a.type] === filter);

  return (
    <div className="bg-white rounded-xl border border-amber-100/60 p-4 md:p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h3 className="font-semibold text-gray-900 text-base">Activity Feed</h3>
          <p className="text-xs text-gray-500 mt-0.5">Live updates across all modules</p>
        </div>
        <button
          onClick={() => navigate("/notifications")}
          className="text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap transition-colors"
          style={{ color: "#d97706" }}
        >
          View all →
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 rounded-xl p-1 flex-shrink-0" style={{ background: "#fef3c7" }}>
        {filterLabels.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
            style={
              filter === f.key
                ? { background: "#f59e0b", color: "#fff" }
                : { color: "#92400e" }
            }
          >
            <span className="w-3 h-3 flex items-center justify-center">
              <i className={`${f.icon} text-xs`} />
            </span>
            <span className="hidden sm:inline">{f.label}</span>
          </button>
        ))}
      </div>

      {/* Feed */}
      <div className="flex-1 space-y-0 overflow-y-auto min-h-0">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-3 pb-3 animate-pulse">
              <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ background: "#fef3c7" }} />
              <div className="flex-1 space-y-1.5 pt-1">
                <div className="h-3 rounded w-full" style={{ background: "#fef3c7" }} />
                <div className="h-2.5 rounded w-24" style={{ background: "#fef3c7" }} />
              </div>
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: "#fef3c7" }}>
              <i className="ri-inbox-line text-xl" style={{ color: "#d97706" }} />
            </div>
            <p className="text-sm text-gray-500">No activity in this category</p>
          </div>
        ) : (
          filtered.slice(0, 10).map((a, i) => {
            const iconCfg = typeIcon[a.type] || { icon: "ri-information-line", color: "#d97706" };
            const isLast = i === Math.min(filtered.length, 10) - 1;
            return (
              <div key={a.id} className="flex gap-3 relative">
                <div className="flex flex-col items-center">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 z-10"
                    style={{ background: iconCfg.color }}
                  >
                    <i className={`${iconCfg.icon} text-sm`} />
                  </div>
                  {!isLast && (
                    <div className="w-px flex-1 my-1" style={{ background: "#fef3c7", minHeight: "12px" }} />
                  )}
                </div>
                <div className="pb-3 flex-1 min-w-0">
                  <p className="text-xs text-gray-700 leading-relaxed">{a.message}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-400">{a.user}</span>
                    <span className="text-gray-200">·</span>
                    <span className="text-xs text-gray-400">{a.time}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
